import { useCallback } from "react";
import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
  useDeleteMutation,
} from "slices/api";
import {
  APPLICATION_COMMENT,
  APPLICATION_DETAILS,
  DOCUMENT_TYPE,
  USER_DOCUMENTS,
} from "tagTypes";

export const useDocumentTypeQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/document-type",
    query,
    tags: [DOCUMENT_TYPE],
  });

export const useLazyDocumentTypeQuery = () => {
  const [getDocumentType, data] = useLazyGetQuery();
  const documentType = async (query = {}) =>
    await getDocumentType(
      {
        endpoint: "/document-type",
        query,
        tags: [DOCUMENT_TYPE],
      },
      true
    ).unwrap();
  return [documentType, data];
};

export const useLazyGetDocumentsQuery = () => {
  const [getDocuments, data] = useLazyGetQuery();
  const document = async (query = {}) =>
    await getDocuments(
      {
        endpoint: "/document",
        query,
        tags: [USER_DOCUMENTS],
      },
      true
    ).unwrap();
  return [document, data];
};

export const useLazyGetHomePageDocumentsQuery = () => {
  const [getHomePageDocuments, data] = useLazyGetQuery();
  const HomePageDocument = async (query = {}) =>
    await getHomePageDocuments(
      {
        endpoint: "/document",
        query,
        tags: [USER_DOCUMENTS],
      },
      true
    ).unwrap();
  return [HomePageDocument, data];
};

export const useDocumentTypesByCategoryIdQuery = (
  documentCategoryId,
  query = {}
) =>
  useGetQuery({
    endpoint: `/document-category/${documentCategoryId}/document-type`,
    query,
    tags: [DOCUMENT_TYPE],
  });

export const useLazyDocumentTypesByCategoryIdQuery = () => {
  const [getDocumentType, data] = useLazyGetQuery();
  const documentType = async (documentCategoryId, query = {}) =>
    await getDocumentType(
      {
        endpoint: `/document-category/${documentCategoryId}/document-type`,
        query,
        tags: [DOCUMENT_TYPE],
      },
      true
    ).unwrap();
  return [documentType, data];
};

export const useDocumentTypesByApplicationIdQuery = (
  applicationTypeId,
  query = {}
) =>
  useGetQuery({
    endpoint: `/application-type/${applicationTypeId}/document-type`,
    query,
    tags: [DOCUMENT_TYPE],
  });

export const useLazyDocumentTypesByApplicationIdQuery = () => {
  const [getDocumentType, data] = useLazyGetQuery();
  const documentType = useCallback(
    async (applicationTypeId, query = {}) =>
      await getDocumentType(
        {
          endpoint: `/application-type/${applicationTypeId}/document-type`,
          query,
          tags: [DOCUMENT_TYPE],
        },
        true
      ).unwrap(),
    [getDocumentType]
  );
  return [documentType, data];
};

export const usePostDocumentTypeMutation = () => {
  const [documentType, data] = usePostMutation();
  const addDocumentType = async (payload, query = {}) =>
    await documentType({
      endpoint: "/document-type",
      query,
      payload: payload || {},
      toastMessage: "Document type added successfully",
      tags: [DOCUMENT_TYPE],
    }).unwrap();
  return [addDocumentType, data];
};

export const usePutDocumentTypeMutation = () => {
  const [documentType, data] = usePutMutation();
  const updateDocumentType = async (payload, id, query = {}) =>
    await documentType({
      endpoint: `/document-type/${id}`,
      query,
      payload: payload || {},
      toastMessage: "Document type updated successfully",
      tags: [DOCUMENT_TYPE],
    }).unwrap();
  return [updateDocumentType, data];
};

export const useDeleteDocumentTypeMutation = () => {
  const [documentType, data] = useDeleteMutation();
  const deleteDocumentType = async (id, query = {}) =>
    await documentType({
      endpoint: `/document-type/${id}`,
      query,
      toastMessage: "Document type deleted successully",
      tags: [DOCUMENT_TYPE],
    }).unwrap();
  return [deleteDocumentType, data];
};

export const usePostUploadPendingDocumentsMutation = () => {
  const [addPendingDocuments, data] = usePostMutation();
  const uploadPendingDocument = async (query, payload) =>
    await addPendingDocuments({
      endpoint: "/application/document",
      query: query || {},
      payload: payload || {},
      toastMessage: "Document uploaded successfully",
      tags: [APPLICATION_DETAILS, DOCUMENT_TYPE],
    }).unwrap();
  return [uploadPendingDocument, data];
};

export const usePostUploadExtraDocumentsMutation = () => {
  const [addExtraDocuments, data] = usePostMutation();
  const uploadExtraDocument = async (payload) =>
    await addExtraDocuments({
      endpoint: "/document/additional-application-document",
      payload: payload || {},
      toastMessage: "Document uploaded successfully",
      tags: [APPLICATION_DETAILS, DOCUMENT_TYPE, APPLICATION_COMMENT],
    }).unwrap();
  return [uploadExtraDocument, data];
};

export const usePostApprovePendingDocumentsMutation = () => {
  const [approvePendingDocuments, data] = usePostMutation();
  const verifyPendingDocument = async (query) =>
    await approvePendingDocuments({
      endpoint: "/application/approve-pending-document",
      query: query || {},
      toastMessage: "Documents approved successfully",
      tags: [APPLICATION_DETAILS, DOCUMENT_TYPE],
    }).unwrap();
  return [verifyPendingDocument, data];
};

export const usePostUploadUserDocuments = () => {
  const [uploadDocuments, data] = usePostMutation();
  const UploadDocument = async (payload) =>
    await uploadDocuments({
      endpoint: "/document",
      payload,
      toastMessage: "Document Uploaded Successfully",
      tags: [USER_DOCUMENTS],
    }).unwrap();
  return [UploadDocument, data];
};
